/**
 * MomentPublic — public preview page reachable at /m/:id.
 *
 * Item G (iter79). Designed so a WhatsApp link paste "just works":
 *   - Logged-out visitor sees a warm preview (title, dates, host city,
 *     creator first name, "N going, M interested") and a clear
 *     "Log in or sign up to join" CTA.
 *   - Logged-in student: after preview loads, quickly bounces them into
 *     the real Moment detail inside Manifest. Same URL still previews
 *     first so opening in an in-app WhatsApp browser feels instant.
 *
 * Privacy: uses only /api/moments/public/{id} which returns first names
 * and aggregate counts. No emails, addresses, or case_ids are exposed.
 */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/lib/auth";
import { MapPin, Calendar, Users, ArrowRight, Compass } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function _fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + (iso.length === 10 ? "T00:00:00Z" : "")).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric", timeZone: "UTC",
    });
  } catch { return iso; }
}

export default function MomentPublic() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/moments/public/${id}`)
      .then(r => setPreview(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Couldn't load this Assembly."));
  }, [id]);

  // A logged-in student is best served inside the app. We land them on the
  // Moment detail modal, still respecting Manifest opt-in gating there.
  useEffect(() => {
    if (user && user.role === "student" && preview) {
      const t = setTimeout(() => {
        navigate(`/dashboard/manifest?tab=moments&open=${preview.id}`);
      }, 1200);
      return () => clearTimeout(t);
    }
  }, [user, preview, navigate]);

  if (err) return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="moment-public-error">
      <div className="text-3xl font-bold text-ink mb-2">Not available</div>
      <p className="text-muted">{err}</p>
      <Link to="/" className="mt-6 inline-flex items-center gap-1.5 text-elec font-semibold">
        Go home <ArrowRight size={14}/>
      </Link>
    </div>
  );

  if (!preview) return (
    <div className="max-w-2xl mx-auto px-4 py-20" data-testid="moment-public-loading">
      <div className="moe-shimmer h-8 w-40 mb-6"/>
      <div className="moe-shimmer h-40 w-full"/>
    </div>
  );

  const totalInterest = (preview.counts?.going || 0) + (preview.counts?.interested || 0);

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 md:py-16" data-testid="moment-public-preview">
      <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold flex items-center gap-1.5">
        <Compass size={12}/> Ministry of Exchange · Assembly
      </div>
      <h1 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-ink" data-testid="moment-public-title">
        {preview.title}
      </h1>
      <p className="mt-2 text-sm text-muted">
        Started by <span className="font-semibold text-ink">{preview.creator_first}</span>
        {preview.host_university ? <> at <span className="font-semibold text-ink">{preview.host_university}</span></> : null}
      </p>

      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        <div className="rounded-2xl border border-ink/12 bg-paper p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold flex items-center gap-1.5">
            <Calendar size={11}/> When
          </div>
          <div className="mt-1 text-base font-semibold text-ink" data-testid="moment-public-dates">
            {_fmtDate(preview.start_date)}{preview.end_date && preview.end_date !== preview.start_date ? ` – ${_fmtDate(preview.end_date)}` : ""}
          </div>
          {preview.date_precision === "flexible" && (
            <div className="mt-1 text-[11px] text-muted">Dates are flexible - members can vote.</div>
          )}
        </div>
        <div className="rounded-2xl border border-ink/12 bg-paper p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold flex items-center gap-1.5">
            <MapPin size={11}/> Where
          </div>
          <div className="mt-1 text-base font-semibold text-ink" data-testid="moment-public-city">
            {preview.host_city || "Not specified"}
          </div>
        </div>
      </div>

      {/* People signal - real counts only, honest empty state */}
      <div className="mt-6 rounded-2xl border border-ink/12 bg-paper p-5" data-testid="moment-public-people">
        <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold flex items-center gap-1.5">
          <Users size={11}/> People
        </div>
        {totalInterest > 0 ? (
          <>
            <div className="mt-1 text-base font-semibold text-ink">
              {preview.counts.going} going{preview.counts.interested > 0 ? `, ${preview.counts.interested} interested` : ""}
            </div>
            {preview.sample_first_names?.length > 0 && (
              <div className="mt-1 text-[12px] text-muted">
                Including {preview.sample_first_names.slice(0, 5).join(", ")}
                {preview.counts.going > preview.sample_first_names.length ? ` and ${preview.counts.going - preview.sample_first_names.length} more` : ""}
              </div>
            )}
          </>
        ) : (
          <div className="mt-1 text-[12px] text-muted">No one has joined yet. Be the first once you're in.</div>
        )}
      </div>

      {/* CTA depending on auth state */}
      <div className="mt-8 rounded-2xl border border-elec/40 bg-[color:var(--moe-mint)]/60 p-5" data-testid="moment-public-cta">
        {user && user.role === "student" ? (
          <>
            <div className="text-sm font-bold text-ink">Opening your Assembly view...</div>
            <p className="mt-1 text-[12px] text-muted">
              You're logged in. Taking you to the Cohort tab where you can Join, mark Interested, or say you can't make it.
            </p>
            <Link
              to={`/dashboard/manifest?tab=moments&open=${preview.id}`}
              data-testid="moment-public-open-in-app"
              className="mt-3 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-ink text-white text-sm font-semibold hover:opacity-90"
            >
              Open in Cohort <ArrowRight size={13}/>
            </Link>
          </>
        ) : (
          <>
            <div className="text-sm font-bold text-ink">Want to join this Assembly?</div>
            <p className="mt-1 text-[12px] text-muted">
              Ministry of Exchange is a private cohort tool for Indian B-school students on exchange. Log in or start your intake first, then you'll be able to Join or mark Interested here.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to={`/login?next=/m/${preview.id}`}
                data-testid="moment-public-login-cta"
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-ink text-white text-sm font-semibold hover:opacity-90"
              >
                Log in to join <ArrowRight size={13}/>
              </Link>
              <Link
                to="/start"
                data-testid="moment-public-signup-cta"
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-ink/25 text-sm font-semibold text-ink hover:border-elec"
              >
                Start your intake
              </Link>
            </div>
          </>
        )}
      </div>

      <div className="mt-8 text-[11px] text-muted leading-snug">
        Ministry of Exchange coordinates real Assemblies for real exchange cohorts. No pooled money, no addresses shared through the app - intent-only.
      </div>
    </div>
  );
}
